import { useEffect, useRef, useState } from 'react';
import type { FC } from 'react';
import { useParams } from 'react-router-dom';
import { Link as RouterLink } from 'react-router-dom';
import { debounce, set } from 'lodash';
import { Box, CircularProgress, InputAdornment, IconButton, Link, TextField, Tooltip, Modal, Chip, FormControlLabel, Checkbox } from '@mui/material';
import SearchIcon from '../../../icons/Search';
import XIcon from '../../../icons/X';
import { TopLevelOpenOrderSearch, defaultTopLevelOpenOrderSearch, TopLevelItemSearch, defaultTopLevelItemSearch } from 'src/types/TopLevelSearch';
import Api from '../../../api/Api';
import HistoryIcon from '@mui/icons-material/History';
import useCustomer from 'src/hooks/useCustomer';
import { LOCAL_STORAGE_KEYS } from 'src/constants';
import ItemSearchResults from './ItemSearchResults';

const ContentSearch: FC<React.PropsWithChildren<unknown>> = () => {
  const [previousSearches, setPreviousSearches] = useState<string[]>([]);
  const [previousViews, setPreviousViews] = useState<any[]>([]);

  const [query, setQuery] = useState<string>('');
  const [open, setOpen] = useState<boolean>(false);

  //loading states
  const [openOrdersSearchIsLoading, setOpenOrderSearchIsLoading] = useState<boolean>(false);
  const [openOrderPOSearchIsLoading, setOpenOrderPOSearchIsLoading] = useState<boolean>(false);

  //show/hide states
  const [showItemResults, setShowItemResults] = useState<boolean>(false);

  const [openOrderResults, setOpenOrderResults] = useState<TopLevelOpenOrderSearch>({ ...defaultTopLevelOpenOrderSearch });
  const [openOrderPOResults, setOpenOrderPOResults] = useState<TopLevelOpenOrderSearch>({ ...defaultTopLevelOpenOrderSearch });
  const [itemResults, setItemResults] = useState<TopLevelItemSearch>({ ...defaultTopLevelItemSearch });
  const [showOpenOrderResults, setShowOpenOrderResults] = useState<boolean>(false);
  const [showOpenOrderPOResults, setShowOpenOrderPOResults] = useState<boolean>(false);

  const [availableOnly, setAvailableOnly] = useState<boolean>(true);

  const { customerNumber } = useParams();
  const { customer } = useCustomer();

  useEffect(() => {
    const json = localStorage.getItem(LOCAL_STORAGE_KEYS.PreviousSearches);
    const jsonViews = localStorage.getItem(LOCAL_STORAGE_KEYS.PreviousViews);
    const searches = JSON.parse(json);
    const views = JSON.parse(jsonViews);
    if (searches) {
      setPreviousSearches(searches);
    }
    if (views) {
      setPreviousViews(views);
    }
  }, []);

  useEffect(() => {
    localStorage.setItem(LOCAL_STORAGE_KEYS.PreviousSearches, JSON.stringify(previousSearches));
  }, [previousSearches]);

  useEffect(() => {
    debouncedSearch.current(query, previousSearches, availableOnly);
  }, [query]);

  useEffect(() => {
    localStorage.setItem(LOCAL_STORAGE_KEYS.PreviousViews, JSON.stringify(previousViews));
  }, [previousViews]);

  const addPreviousSearch = (search, prevSearches): void => {
    if (search.length < 5) return;
    let uniqueSearches = [...new Set([search, ...prevSearches])];
    if (uniqueSearches.length > 5) uniqueSearches = uniqueSearches.slice(0, 5);
    setPreviousSearches(uniqueSearches);
  };

  const search = async (q: string, prevSearches: string[], isAvailableOnly: boolean): Promise<void> => {
    if (q === '') return;

    searchOpenOrder(q);
    searchOpenOrderPO(q);
    searchItem(q, isAvailableOnly);
    addPreviousSearch(q, prevSearches);
  };

  const searchOpenOrder = async (term: string) => {
    setShowOpenOrderResults(false);
    setOpenOrderSearchIsLoading(true);

    let o = await Api.get<TopLevelOpenOrderSearch>(`/search/toplevel/openorder?query=${encodeURIComponent(term)}`);
    setOpenOrderResults(o);

    setOpenOrderSearchIsLoading(false);
    setShowOpenOrderResults(true);
  };

  const searchOpenOrderPO = async (term: string) => {
    setShowOpenOrderPOResults(false);
    setOpenOrderPOSearchIsLoading(true);

    let p = await Api.get<TopLevelOpenOrderSearch>(`/search/toplevel/openorder/vendorpo?query=${encodeURIComponent(term)}`);
    setOpenOrderPOResults(p);

    setOpenOrderPOSearchIsLoading(false);
    setShowOpenOrderPOResults(true);
  };

  const searchItem = async (term: string, isAvailableOnly: boolean) => {
    setShowItemResults(false);

    let i = await Api.get<TopLevelItemSearch>(`/search/toplevel/item?query=${encodeURIComponent(term)}&availableOnly=${isAvailableOnly}`);
    setItemResults(i);
    setShowItemResults(true);
  };

  const debouncedSearch = useRef(debounce((q, prevSearches, isAvailableOnly) => search(q, prevSearches, isAvailableOnly), 500));

  const handleClick = (item: string): void => {
    setQuery(item);
  };

  const addPreviousViewed = (view): void => {
    let uniqueViews = [...new Set([view, ...previousViews])];
    if (uniqueViews.length > 10) uniqueViews = uniqueViews.slice(0, 9);
    setPreviousViews(uniqueViews);
  };
  const handleLinkClick = (link): void => {
    addPreviousViewed(link);
  };

  const handleDelete = (item: string): void => {
    var oldPrevSearch = [...previousSearches];
    oldPrevSearch.splice(previousSearches.indexOf(previousSearches.find((el) => el === item)), 1);
    setPreviousSearches(oldPrevSearch);
  };

  const handleSeachOpen = (): void => {
    setQuery('');
    setOpen(true);
    debouncedSearch.current(query, previousSearches, availableOnly);
  };

  const handleKeyUp = (e): void => {
    if (e.key === 'Enter') {
      debouncedSearch.current(query, previousSearches, availableOnly);
    }
  };

  const handleEscKey = (e): void => {
    if (e.key === 'Escape') {
      setOpen(false);
    }
  };

  const handleAvailability = (): void => {
    setAvailableOnly(!availableOnly);
    debouncedSearch.current(query, previousSearches, !availableOnly);
  };

  const orderNumberMatch: Boolean = query && query.length > 4 && openOrderResults.openOrders?.some((order) => order.fullOrderNumber?.substring(0, 4) === query.substring(0, 4));

  return (
    <>
      <Tooltip title="Search">
        <IconButton color="inherit" onClick={handleSeachOpen}>
          <SearchIcon fontSize="small" />
        </IconButton>
      </Tooltip>

      <Modal onClose={() => setOpen(false)} open={open} className="content-search__modal">
        <div>
          <div className="content-search__modal__content">
            <IconButton style={{ float: 'right' }} onClick={() => setOpen(false)} className="content-search__modal__close">
              <XIcon fontSize="small" />
            </IconButton>
            <div className="content-search__modal__content__search-box-container">
              <TextField
                className="content-search__modal__content__search-box"
                variant="standard"
                fullWidth
                placeholder="Search..."
                onKeyDown={(e: any) => {
                  e.stopPropagation();
                  handleEscKey(e);
                }}
                InputProps={{
                  type: 'search',
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
                inputRef={(input) => input && input.focus()}
                onChange={(event): void => {
                  setQuery(event.target.value);
                }}
                onKeyUp={handleKeyUp}
                value={query}
              />
            </div>
            <div>
              <FormControlLabel control={<Checkbox name="availableOnly" onChange={handleAvailability} checked={availableOnly} size="small" />} label="Available Items Only" />
            </div>
            <div className="content-search__modal__content__results">
              {previousSearches?.length ? (
                <div className="content-search__modal__content__results-group content-search__modal__content__results__recent-search">
                  <h2>Recent Searches</h2>
                  <div>
                    {previousSearches.map((item, i) => {
                      return <Chip label={item} onDelete={() => handleDelete(item)} key={i} onClick={() => handleClick(item)} sx={{ mr: 1 }} />;
                    })}
                  </div>
                </div>
              ) : (
                <></>
              )}

              {previousViews?.length ? (
                <div className="content-search__modal__content__results-group">
                  <h2>Recently Viewed</h2>
                  {previousViews.map((view, i) => {
                    return (
                      <Link key={i} target="_blank" component={RouterLink} to={view.linkTo} className="content-search__modal__content__results__list-item">
                        <div className="contentSearchImageDiv">
                          <HistoryIcon />
                          <b>{view.item.itemNumber}</b> <span>- {view.item.name}</span>
                        </div>
                      </Link>
                    );
                  })}
                </div>
              ) : (
                <></>
              )}

              <ItemSearchResults
                showItemResults={showItemResults && !!query && !orderNumberMatch}
                itemResults={itemResults}
                customerNumber={customerNumber}
                customer={customer}
                handleLinkClick={handleLinkClick}
              />

              {openOrdersSearchIsLoading && (
                <>
                  <h2>Current Open Orders</h2>
                  <CircularProgress size={20} sx={{ color: '#777' }} />
                </>
              )}

              {showOpenOrderResults && openOrderResults.openOrders?.length > 0 && (
                <div className="content-search__modal__content__results-group content-search__modal__content__results__recent-orders">
                  <h2>Current Open Orders</h2>
                  {openOrderResults.openOrders.map((order, index) => {
                    const linkTo = `/${order.customerNumber}/${order.orderNumber}`;

                    return (
                      <Link key={index} target="_parent" component={RouterLink} to={linkTo} className="content-search__modal__content__results__list-item">
                        <b>{order.fullOrderNumber}</b> {order.customerPO ? `- ${order.customerPO}` : ''}
                      </Link>
                    );
                  })}
                </div>
              )}

              {openOrderPOSearchIsLoading && (
                <>
                  <h2>Vendor Purchase Orders</h2>
                  <CircularProgress size={20} sx={{ color: '#777' }} />
                </>
              )}

              {showOpenOrderPOResults && openOrderPOResults.openOrders?.length > 0 && (
                <div className="content-search__modal__content__results-group content-search__modal__content__results__recent-orders">
                  <h2>Vendor Purchase Orders</h2>
                  {openOrderPOResults.openOrders.map((order, index) => {
                    const linkTo = `/${order.customerNumber}/${order.orderNumber}`;

                    return (
                      <Link key={index} target="_parent" component={RouterLink} to={linkTo} className="content-search__modal__content__results__list-item">
                        <b>{order.fullOrderNumber}</b> - {order.vendorPO}
                      </Link>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default ContentSearch;
